import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Buildings, UsersThree, Trash, Plus, Briefcase, Target, CaretRight } from "@phosphor-icons/react";

export default function RecruiterClients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [pools, setPools] = useState([]);
  const [cName, setCName] = useState("");
  const [pName, setPName] = useState("");
  const [pClient, setPClient] = useState("");
  const [busy, setBusy] = useState(false);

  // Client -> Jobs -> Pools -> Matching
  const [selected, setSelected] = useState(null); // client object
  const [jobs, setJobs] = useState([]);
  const [jTitle, setJTitle] = useState("");
  const [jText, setJText] = useState("");
  const [matchFor, setMatchFor] = useState(null); // job id being matched
  const [matchPool, setMatchPool] = useState("");
  const [matchResult, setMatchResult] = useState(null);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/recruiter/clients"), api.get("/recruiter/pools")]);
      setClients(c.data.clients || []);
      setPools(p.data.pools || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);
  if (user && user.role !== "recruiter" && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const loadJobs = async (client) => {
    setSelected(client); setMatchFor(null); setMatchResult(null);
    try { const r = await api.get(`/recruiter/clients/${client.id}/jobs`); setJobs(r.data.jobs || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addClient = async () => {
    if (!cName.trim()) return;
    setBusy(true);
    try { await api.post("/recruiter/clients", { name: cName }); setCName(""); toast.success("Client added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const delClient = async (id) => {
    try { await api.delete(`/recruiter/clients/${id}`); toast.success("Client removed"); if (selected?.id === id) { setSelected(null); setJobs([]); } load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const addPool = async () => {
    if (!pName.trim()) return;
    setBusy(true);
    try { await api.post("/recruiter/pools", { name: pName, client_id: pClient || null }); setPName(""); setPClient(""); toast.success("Pool created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const delPool = async (id) => {
    try { await api.delete(`/recruiter/pools/${id}`); toast.success("Pool removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addJob = async () => {
    if (!selected) return;
    if (!jTitle.trim()) { toast.error("Role title is required"); return; }
    setBusy(true);
    try {
      let criteria = { title: jTitle.trim() };
      if (jText.trim()) {
        try {
          const pr = await api.post("/recruiter/parse-role", { text: jText.trim() });
          criteria = { ...(pr.data.criteria || {}), title: jTitle.trim() };
        } catch { criteria.description = jText.trim(); }
      }
      await api.post(`/recruiter/clients/${selected.id}/jobs`, criteria);
      setJTitle(""); setJText(""); toast.success("Role added");
      loadJobs(selected); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const delJob = async (id) => {
    try { await api.delete(`/recruiter/jobs/${id}`); toast.success("Role removed"); loadJobs(selected); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const runMatch = async (jobId) => {
    if (!matchPool) { toast.error("Pick a pool to match against"); return; }
    setBusy(true); setMatchResult(null);
    try {
      const r = await api.post(`/recruiter/pools/${matchPool}/match`, { job_id: jobId });
      const f = r.data.funnel || {};
      setMatchResult({ jobId, ...r.data });
      toast.success(`${f.eligible || 0} eligible · ${f.possibly_eligible || 0} possibly · ${r.data.pool_size || 0} in pool`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const invite = async (candidateId, job) => {
    try {
      await api.post("/b2b/invite", { candidate_id: candidateId, job_id: job.id, role_title: job.title, type: "invite_to_apply" });
      toast.success("Invitation sent — the candidate controls what they share back.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const clientPools = pools.filter((p) => !selected || p.client_id === selected.id);

  return (
    <div data-testid="recruiter-clients-page">
      <PageHeader title="Clients, roles & pools" subtitle="Clients → Jobs → Candidate Pools → Matching. Matching always uses the same eligibility-first engine as direct employers." />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Clients */}
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="clients-section">
          <div className="mb-3 flex items-center gap-2 text-slate-800"><Buildings weight="fill" /><h2 className="font-bold">Clients</h2></div>
          <div className="flex gap-2">
            <Input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="New client name" data-testid="client-name-input" />
            <Button onClick={addClient} disabled={busy} data-testid="add-client-btn" className="bg-slate-900 hover:bg-slate-800 gap-1"><Plus /> Add</Button>
          </div>
          <ul className="mt-4 space-y-2">
            {clients.length === 0 && <li className="text-sm text-slate-400">No clients yet.</li>}
            {clients.map((c) => (
              <li key={c.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 cursor-pointer ${selected?.id === c.id ? "border-slate-900 bg-slate-50" : "border-slate-100 hover:border-slate-300"}`}
                  onClick={() => loadJobs(c)} data-testid={`client-row-${c.id}`}>
                <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <CaretRight weight="bold" className={`text-slate-400 transition-transform ${selected?.id === c.id ? "rotate-90" : ""}`} />
                  {c.name} <span className="text-xs text-slate-400">· {c.job_count || 0} role(s) · {c.pool_count} pool(s)</span>
                </span>
                <button onClick={(e) => { e.stopPropagation(); delClient(c.id); }} data-testid={`del-client-${c.id}`} className="text-slate-400 hover:text-red-500"><Trash /></button>
              </li>
            ))}
          </ul>
        </section>

        {/* Pools */}
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pools-section">
          <div className="mb-3 flex items-center gap-2 text-slate-800"><UsersThree weight="fill" /><h2 className="font-bold">Candidate pools</h2></div>
          <div className="space-y-2">
            <Input value={pName} onChange={(e) => setPName(e.target.value)} placeholder="New pool name" data-testid="pool-name-input" />
            <div className="flex gap-2">
              <select value={pClient} onChange={(e) => setPClient(e.target.value)} data-testid="pool-client-select"
                className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="">No client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <Button onClick={addPool} disabled={busy} data-testid="add-pool-btn" className="bg-slate-900 hover:bg-slate-800 gap-1"><Plus /> Create</Button>
            </div>
          </div>
          <ul className="mt-4 space-y-2">
            {pools.length === 0 && <li className="text-sm text-slate-400">No pools yet.</li>}
            {pools.map((p) => (
              <li key={p.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`pool-row-${p.id}`}>
                <span className="text-sm font-medium text-slate-700">{p.name}
                  <span className="text-xs text-slate-400"> · {p.candidate_count} candidate(s){p.client_name ? ` · ${p.client_name}` : ""}</span></span>
                <button onClick={() => delPool(p.id)} data-testid={`del-pool-${p.id}`} className="text-slate-400 hover:text-red-500"><Trash /></button>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-400">Add candidates to a pool from the shortlist in your Workspace. A candidate can belong to several pools.</p>
        </section>
      </div>

      {/* Client roles & matching */}
      {selected && (
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="client-jobs-section">
          <div className="mb-3 flex items-center gap-2 text-slate-800">
            <Briefcase weight="fill" /><h2 className="font-bold">{selected.name} — roles</h2>
          </div>
          <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
            <Input value={jTitle} onChange={(e) => setJTitle(e.target.value)} placeholder="Role title (e.g. Backend Engineer)" data-testid="job-title-input" />
            <Textarea rows={1} value={jText} onChange={(e) => setJText(e.target.value)} placeholder="Optional: paste the role description — we'll structure it" data-testid="job-text-input" className="min-h-[40px]" />
            <Button onClick={addJob} disabled={busy} data-testid="add-job-btn" className="bg-slate-900 hover:bg-slate-800 gap-1"><Plus /> Add role</Button>
          </div>

          <ul className="mt-4 space-y-3">
            {jobs.length === 0 && <li className="text-sm text-slate-400">No roles for this client yet.</li>}
            {jobs.map((j) => (
              <li key={j.id} className="rounded-lg border border-slate-100 p-3" data-testid={`job-row-${j.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-800">{j.title}
                    {j.country ? <span className="text-xs font-normal text-slate-400"> · {j.country}</span> : null}
                    {j.work_arrangement ? <span className="text-xs font-normal text-slate-400"> · {j.work_arrangement}</span> : null}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-1 border-slate-300" data-testid={`match-job-btn-${j.id}`}
                      onClick={() => { setMatchFor(matchFor === j.id ? null : j.id); setMatchResult(null); }}>
                      <Target weight="bold" /> Match
                    </Button>
                    <button onClick={() => delJob(j.id)} data-testid={`del-job-${j.id}`} className="text-slate-400 hover:text-red-500"><Trash /></button>
                  </div>
                </div>

                {matchFor === j.id && (
                  <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3" data-testid={`match-panel-${j.id}`}>
                    <div className="flex gap-2">
                      <select value={matchPool} onChange={(e) => setMatchPool(e.target.value)} data-testid="match-pool-select"
                        className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm">
                        <option value="">Select a candidate pool…</option>
                        {clientPools.map((p) => <option key={p.id} value={p.id}>{`${p.name} (${p.candidate_count})`}</option>)}
                      </select>
                      <Button onClick={() => runMatch(j.id)} disabled={busy} data-testid="run-match-btn" className="bg-slate-900 hover:bg-slate-800">Run match</Button>
                    </div>
                    {matchResult && matchResult.jobId === j.id && (
                      <div className="mt-3" data-testid="match-result">
                        <p className="text-xs text-slate-500 mb-2">
                          Pool: {matchResult.pool_size} · Eligible: <b className="text-emerald-700">{matchResult.funnel?.eligible || 0}</b> · Possibly: <b className="text-amber-700">{matchResult.funnel?.possibly_eligible || 0}</b> · Shortlisted: {matchResult.funnel?.shortlisted || 0}
                        </p>
                        <ul className="space-y-1.5">
                          {(matchResult.candidates || []).slice(0, 8).map((c) => (
                            <li key={c.candidate_id} className="flex items-center justify-between gap-2 text-sm border-b border-slate-100 pb-1">
                              <span className="text-slate-700 min-w-0 truncate">{c.name} <span className="text-xs text-slate-400">{c.headline || ""}</span></span>
                              <span className="flex items-center gap-2 shrink-0">
                                <span className={`text-xs font-semibold ${c.eligibility_status === "eligible" ? "text-emerald-700" : "text-amber-700"}`}>{c.eligibility_status} · {c.match_score}%</span>
                                <Button variant="outline" size="sm" className="h-7 border-slate-300 text-xs" data-testid={`invite-btn-${c.candidate_id}`} onClick={() => invite(c.candidate_id, j)}>Invite</Button>
                              </span>
                            </li>
                          ))}
                          {(matchResult.candidates || []).length === 0 && <li className="text-sm text-slate-400">No eligible candidates in this pool for this role.</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-400">Same central engine as direct employers: Eligibility → Capability → Preference → Practical. The recruiter workflow differs, the matching logic does not.</p>
        </section>
      )}
    </div>
  );
}
